import {
  SummaryFeatureList,
  SummaryFeatureTransformer,
  SummaryFeatureRuleMap,
  FeatureList,
} from '~pricing/transformers/summary/types';
import FeatureRules from '~pricing/transformers/summary/rules/featureRules';
import DefaultIndividualsFeatureList from '~pricing/transformers/summary/features/defaultIndividualsFeatureList';
import TeamsFeatureList from '~pricing/transformers/summary/features/defaultTeamsFeatureList';

import summaryFeaturesTransformer from '~pricing/transformers/summary/summaryFeaturesTransformer';

type Transformer = {
  // internal to this class only
  defaultIndividualList: SummaryFeatureList;
  defaultTeamsList: SummaryFeatureList;
  featureRules: SummaryFeatureRuleMap;
  individualSummaryFeatureTransform(featureListConfig: FeatureList): SummaryFeatureTransformer;
  teamsSummaryFeatureTransform(featureListConfig: FeatureList): SummaryFeatureTransformer;
};

/**
 * The SummaryTransformer Singleton class defines the `getInstance` method that lets clients access
 * the unique singleton instance.
 */
export default class SummaryTransformer implements Transformer {
  private static instance: SummaryTransformer;

  // overridden thru the config
  private readonly _defaultIndividualList: SummaryFeatureList = DefaultIndividualsFeatureList;

  // overridden thru the config, experimental feature rules also present here
  private readonly _featureRules: SummaryFeatureRuleMap = FeatureRules;

  private _defaultTeamsList: SummaryFeatureList = TeamsFeatureList;

  /**
   * The SummaryTransformer Singleton's constructor should always be private to prevent direct
   * construction calls with the `new` operator.
   */

  /* eslint no-empty-function: ["error", { "allow": ["constructors"] }] */
  private constructor() {}

  public get defaultIndividualList(): SummaryFeatureList {
    return this._defaultIndividualList;
  }

  public get featureRules(): SummaryFeatureRuleMap {
    return this._featureRules;
  }

  public get defaultTeamsList(): SummaryFeatureList {
    return this._defaultTeamsList;
  }

  public set defaultTeamsList(value: SummaryFeatureList) {
    this._defaultTeamsList = value;
  }

  /**
   * The static method that controls the access to the singleton instance.
   * This implementation let you subclass the SummaryTransformer Singleton class while keeping
   * just one instance of each subclass around.
   */
  public static getInstance(): SummaryTransformer {
    if (!SummaryTransformer.instance) {
      SummaryTransformer.instance = new SummaryTransformer();
    }
    return SummaryTransformer.instance;
  }

  public individualSummaryFeatureTransform(featureListConfig: FeatureList): SummaryFeatureTransformer {
    if (featureListConfig?.includesDefaultList) {
      return summaryFeaturesTransformer([...featureListConfig.list], {
        ...this.featureRules,
        ...featureListConfig.rules,
      });
    }
    return summaryFeaturesTransformer([...this._defaultIndividualList, ...(featureListConfig?.list ?? [])], {
      ...this.featureRules,
      ...featureListConfig?.rules,
    });
  }

  public teamsSummaryFeatureTransform(featureListConfig: FeatureList): SummaryFeatureTransformer {
    if (featureListConfig?.includesDefaultList) {
      return summaryFeaturesTransformer([...featureListConfig.list], {
        ...this.featureRules,
        ...featureListConfig.rules,
      });
    }
    return summaryFeaturesTransformer([...this._defaultTeamsList, ...(featureListConfig?.list ?? [])], {
      ...this.featureRules,
      ...featureListConfig?.rules,
    });
  }
}
